import CardAboutModal from "@/components/CardAboutModal";
import ClientSongcard from "@/components/ClientSongcard";
import DiscoverIconSM from "@/components/DiscoverIconSm";
import Loading from "@/components/Loading";
import PlayBox from "@/components/PlayBox";
import ThisIsModal from "@/components/ThisIsModal";
import Thumbnail from "@/components/Thumbnails";
import { apiUrl } from "@/constants";
import { BlogPost as BlogPostType } from "@/types";
import {
  Card,
  CardBody,
  Center,
  Container,
  Divider,
  HStack,
  Heading,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import parse, { DOMNode, Element } from "html-react-parser";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRef, useState } from "react";

const metaContent = "Stories from the humans that make it all possible";

export default function BlogPost() {
  const { colorMode } = useColorMode();
  const ref = useRef(null);
  const [modelOpen, setModalOpen] = useState(false);

  const router = useRouter();
  const { slug } = router.query;

  const {
    data: post,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/ninja/blog/${slug}`);
      const data: BlogPostType = await res.json();
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) return <Loading />;
  if (isError || !post) return <Center>Post not found.</Center>;

  return (
    <div>
      <Head>
        <title>{post.title}</title>
        <link rel="icon" href="/songcards.png" />
        <meta name="description" content={metaContent} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaContent} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://songcards.io/blog/${post.slug}`}
        />
        <meta property="og:site_name" content="Songcards" />
        {post.image && <meta property="og:image" content={post.image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaContent} />
        <meta
          name="twitter:url"
          content={`https://songcards.io/blog/${post.slug}`}
        />
        {post.image && <meta name="twitter:image" content={post.image} />}
        <meta name="twitter:site" content="@unlockyoursound" />
        <meta name="twitter:creator" content="@unlockyoursound" />
        <meta name="twitter:image:alt" content={post.title} />
      </Head>

      <Container maxW="lg" p={4} mb={100}>
        {post.image && (
          <Center>
            <Image
              src={post.image}
              alt={post.title}
              width={200}
              height={200}
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                objectFit: "cover",
                marginBottom: 50,
                borderWidth: 2,
                borderColor: "white",
                borderStyle: "solid",
              }}
            />
          </Center>
        )}
        {post.linked_card && (
          <Center>
            <PlayBox card={post.linked_card} />
          </Center>
        )}
        <Divider mt={10} mb={5} />
        <br />
        <Heading>{post.title}</Heading>
        <br />
        <div className="blog-post-content">
          <div>
            {parse(`${post.content}`, {
              replace(domNode: DOMNode) {
                if (
                  domNode instanceof Element &&
                  domNode.name === "pre" &&
                  domNode.firstChild &&
                  "data" in domNode.firstChild
                ) {
                  return <ClientSongcard uuid={domNode.firstChild.data} />;
                } else {
                  return { domNode };
                }
              },
            })}
          </div>
        </div>
        <Divider mt={10} mb={5} />
        <Center mt={0} mb={5}>
          <Heading as={"h3"} size={"lg"} mt={5} mb={5}>
            Featured Songcard
          </Heading>
        </Center>
        <div ref={ref}>
          <Center>
            <Card
              maxW="xs"
              align="center"
              bgColor={colorMode == "dark" ? "black" : "white"}
              variant="outline"
              borderColor={colorMode == "dark" ? "white" : "black"}
              borderStyle="dotted"
              borderRadius={15}
              shadow={"md"}
            >
              <CardBody>
                <div onClick={() => setModalOpen(true)}>
                  <Thumbnail
                    ext_file={post.linked_card.ext_file}
                    comp_file={post.linked_card.comp_file}
                    image={post.linked_card.image}
                  />
                </div>
                <br />
                <Center>
                  <PlayBox card={post.linked_card} />
                </Center>
                <Text ml={4} mb={3} mt={5}>
                  is a Songcard from
                  <br />
                  {post.linked_card.primary_artist && (
                    <Text as="b" key={post.linked_card.primary_artist.slug}>
                      <Link
                        href={`/artists/${post.linked_card.primary_artist.slug}`}
                      >
                        {post.linked_card.primary_artist.name}
                      </Link>
                    </Text>
                  )}
                </Text>
                <Divider mb={5} />
                <Center>
                  <HStack justify="space-between" w="94%">
                    <CardAboutModal
                      card={post.linked_card}
                      price={false}
                      showOpen={true}
                    />
                    <ThisIsModal
                      card={post.linked_card}
                      price={false}
                      showOpen={modelOpen}
                      setModalOpen={setModalOpen}
                    />
                  </HStack>
                </Center>
              </CardBody>
            </Card>
          </Center>
        </div>
        <DiscoverIconSM />
      </Container>
    </div>
  );
}

export async function getStaticProps({ params }: { params: { slug: string } }) {
  const { slug } = params;

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/ninja/blog/${slug}`);
      const data: BlogPostType = await res.json();
      return data;
    },
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths() {
  const endpoint = `${apiUrl}/ninja/blogs`;

  const response = await fetch(endpoint);
  const blogs = await response.json();
  const paths = await blogs.map((post: BlogPostType) => ({
    params: { slug: post.slug },
  }));

  return { paths, fallback: "blocking" };
}
